/* eslint-disable camelcase */
import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import { Node, Edge } from '@xyflow/react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import { aiTokens } from '../../admin/Workflow/aiGeneratorTokens'
import { themeColor, xxllncColor } from '../../layout/theme/color'
import { getUrl, postJson } from '../../providers/data'
import { CopilotMessage, CopilotResponse, WorkflowDiff } from '../../types/apiRequests'

// `normaliseDiff` guarantees every collection is present, so a Required view avoids optional-chaining without a new DTO.
type DiffView = Required<Omit<WorkflowDiff, 'explanation'>> & { explanation: string }

const normaliseDiff = (diff: WorkflowDiff): DiffView => ({
  add_nodes: diff.add_nodes ?? [],
  update_nodes: diff.update_nodes ?? [],
  remove_node_ids: diff.remove_node_ids ?? [],
  add_edges: diff.add_edges ?? [],
  remove_edges: diff.remove_edges ?? [],
  explanation: diff.explanation ?? '',
})

interface CopilotPanelProps {
  workflowId: string
  nodes: Node[]
  edges: Edge[]
  onApply: (nodes: Node[], edges: Edge[]) => void
  onClose: () => void
}

const PANEL_WIDTH = 380

const dataNodeType = (data: Record<string, unknown>): string | undefined => {
  const t = data?.type
  return typeof t === 'string' ? t : undefined
}

const applyDiffToGraph = (
  nodes: Node[],
  edges: Edge[],
  diff: DiffView,
): { nodes: Node[]; edges: Edge[] } => {
  const removeEdgePairs = new Set(diff.remove_edges.map((e) => `${e.source}->${e.target}`))
  let nextEdges = edges.filter((e) => !removeEdgePairs.has(`${e.source}->${e.target}`))

  const removeNodeIds = new Set(diff.remove_node_ids)
  let nextNodes = nodes.filter((n) => !removeNodeIds.has(n.id))
  nextEdges = nextEdges.filter(
    (e) => !removeNodeIds.has(e.source) && !removeNodeIds.has(e.target),
  )

  for (const add of diff.add_nodes) {
    const data = add.data ?? {}
    const newNode: Node = {
      id: add.id,
      type: add.type ?? dataNodeType(data) ?? 'tool_call',
      data: { ...data },
      position: { x: 0, y: 0 },
    }
    nextNodes = [...nextNodes, newNode]
  }

  if (diff.update_nodes.length > 0) {
    const patchById = new Map(diff.update_nodes.map((p) => [p.id, p.data ?? {}]))
    nextNodes = nextNodes.map((n) => {
      const patch = patchById.get(n.id)
      if (!patch) return n
      return { ...n, data: { ...(n.data ?? {}), ...patch } }
    })
  }

  for (const add of diff.add_edges) {
    const newEdge: Edge = {
      id: add.id,
      source: add.source,
      target: add.target,
      sourceHandle: add.sourceHandle ?? undefined,
      targetHandle: add.targetHandle ?? undefined,
      type: 'workflow',
    }
    nextEdges = [...nextEdges, newEdge]
  }

  return { nodes: nextNodes, edges: nextEdges }
}

const DiffCounters: FC<{ diff: DiffView }> = ({ diff }) => {
  const translate = useTranslate()
  const addCount = diff.add_nodes.length + diff.add_edges.length
  const removeCount = diff.remove_node_ids.length + diff.remove_edges.length
  const items = [
    { color: '#0F7B5C', bg: 'rgba(15,123,92,0.12)', label: translate('workflow.copilot.diffAdd'), n: addCount },
    { color: '#B45309', bg: 'rgba(180,83,9,0.12)', label: translate('workflow.copilot.diffUpdate'), n: diff.update_nodes.length },
    { color: '#B91C1C', bg: 'rgba(185,28,28,0.12)', label: translate('workflow.copilot.diffRemove'), n: removeCount },
  ]
  return (
    <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
      {items.map((item) => (
        <Chip
          key={item.label}
          size="small"
          label={`${item.label} · ${item.n}`}
          sx={{
            'height': 22,
            'fontSize': 11,
            'fontWeight': 600,
            'color': item.color,
            'background': item.bg,
            'border': 'none',
            '& .MuiChip-label': { px: 1 },
          }}
        />
      ))}
    </Stack>
  )
}

const CopilotPanel: FC<CopilotPanelProps> = ({
  workflowId,
  nodes,
  edges,
  onApply,
  onClose,
}) => {
  const translate = useTranslate()
  const [history, setHistory] = useState<CopilotMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pendingDiff, setPendingDiff] = useState<DiffView | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  // Guard against state updates after unmount (closing the panel mid-LLM-call would otherwise leak a stale pendingDiff).
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [history.length, pendingDiff, loading])

  const submit = async (event?: FormEvent) => {
    event?.preventDefault()
    const trimmed = input.trim()
    if (!trimmed || loading) return
    setInput('')
    setError(null)
    setPendingDiff(null)
    const nextHistory: CopilotMessage[] = [...history, { role: 'user', content: trimmed }]
    setHistory(nextHistory)
    setLoading(true)
    try {
      const result = await postJson<CopilotResponse>(
        `${getUrl()}/workflow/${workflowId}/copilot`,
        { messages: nextHistory },
      )
      if (!isMountedRef.current) return
      setHistory([...nextHistory, { role: 'assistant', content: result.reply }])
      if (result.diff) setPendingDiff(normaliseDiff(result.diff))
    } catch (e) {
      if (!isMountedRef.current) return
      const message = e instanceof Error ? e.message : String(e)
      setError(message)
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }

  const applyPending = () => {
    if (!pendingDiff) return
    const { nodes: nextNodes, edges: nextEdges } = applyDiffToGraph(nodes, edges, pendingDiff)
    onApply(nextNodes, nextEdges)
    setPendingDiff(null)
  }

  const discardPending = () => setPendingDiff(null)

  const seedPrompt = (value: string) => {
    setInput(value)
  }

  const suggestions = [
    translate('workflow.copilot.suggestRetry'),
    translate('workflow.copilot.suggestSummary'),
    translate('workflow.copilot.suggestParallel'),
  ]

  return (
    <Box
      sx={{
        width: PANEL_WIDTH,
        flexShrink: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        borderLeft: `1px solid ${themeColor.slate10}`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${themeColor.slate10}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '10px',
            background: aiTokens.accentSoft,
            color: aiTokens.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: themeColor.slate }}>
            {translate('workflow.copilot.title')}
          </Typography>
          <Typography sx={{ fontSize: 11, color: themeColor.slate60, lineHeight: 1.2 }}>
            {translate('workflow.copilot.tagline')}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={translate('ra.action.close')}
          sx={{ color: themeColor.slate60 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ height: 2, background: aiTokens.gradient, flexShrink: 0 }} />

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          py: 2,
        }}
      >
        {history.length === 0 && !loading && (
          <Stack spacing={1.5}>
            <Typography variant="body2" sx={{ color: themeColor.slate60, lineHeight: 1.55 }}>
              {translate('workflow.copilot.intro')}
            </Typography>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: themeColor.slate40,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  mb: 0.75,
                  display: 'block',
                }}
              >
                {translate('workflow.copilot.try')}
              </Typography>
              <Stack spacing={0.5}>
                {suggestions.map((sug) => (
                  <Box
                    key={sug}
                    component="button"
                    type="button"
                    onClick={() => seedPrompt(sug)}
                    sx={{
                      'all': 'unset',
                      'cursor': 'pointer',
                      'p': 1,
                      'borderRadius': '8px',
                      'fontSize': 12,
                      'lineHeight': 1.45,
                      'color': themeColor.slate80,
                      'background': themeColor.slate05,
                      'border': `1px solid ${themeColor.slate10}`,
                      'transition': 'all 0.15s ease',
                      '&:hover': {
                        background: aiTokens.accentSoft,
                        borderColor: aiTokens.accentBorder,
                        color: aiTokens.accentHover,
                      },
                    }}
                  >
                    {sug}
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        )}

        {history.map((turn, idx) => {
          const isUser = turn.role === 'user'
          return (
            <Box
              key={`${idx}-${turn.role}`}
              sx={{
                mb: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: '10px',
                background: isUser ? themeColor.slate05 : aiTokens.accentSoft,
                border: isUser
                  ? `1px solid ${themeColor.slate10}`
                  : `1px solid ${aiTokens.accentBorder}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  color: isUser ? themeColor.slate60 : aiTokens.accent,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  mb: 0.5,
                }}
              >
                {isUser
                  ? translate('workflow.copilot.you')
                  : translate('workflow.copilot.assistant')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5 }}
              >
                {turn.content}
              </Typography>
            </Box>
          )
        })}

        {pendingDiff && (
          <Box
            sx={{
              mb: 1.5,
              border: `1px solid ${aiTokens.accentBorder}`,
              borderRadius: '12px',
              overflow: 'hidden',
              background: '#fff',
              boxShadow: '0 1px 4px rgba(99,102,241,0.08)',
            }}
          >
            <Box sx={{ height: 2, background: aiTokens.gradient }} />
            <Box sx={{ p: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  color: aiTokens.accent,
                  letterSpacing: '0.03em',
                  textTransform: 'uppercase',
                  fontSize: 10,
                  mb: 0.5,
                }}
              >
                {translate('workflow.copilot.pendingDiff')}
              </Typography>
              {pendingDiff.explanation && (
                <Typography variant="body2" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  {pendingDiff.explanation}
                </Typography>
              )}
              <DiffCounters diff={pendingDiff} />
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyPending}
                  sx={{
                    'textTransform': 'none',
                    'background': aiTokens.accent,
                    'color': '#fff',
                    'fontWeight': 600,
                    'boxShadow': 'none',
                    '&:hover': { background: aiTokens.accentHover, boxShadow: 'none' },
                  }}
                >
                  {translate('workflow.copilot.apply')}
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={discardPending}
                  sx={{ textTransform: 'none', color: themeColor.slate60 }}
                >
                  {translate('workflow.copilot.discard')}
                </Button>
              </Stack>
            </Box>
          </Box>
        )}

        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: aiTokens.accent }}>
            <CircularProgress size={14} sx={{ color: aiTokens.accent }} />
            <Typography variant="caption" sx={{ fontSize: 12 }}>
              {translate('workflow.copilot.thinking')}
            </Typography>
          </Stack>
        )}

        {error && (
          <Box
            sx={{
              mt: 1,
              p: 1.25,
              borderRadius: '8px',
              background: aiTokens.errorBg,
              border: `1px solid ${aiTokens.errorBorder}`,
            }}
          >
            <Typography variant="body2" sx={{ color: xxllncColor.red100, fontSize: 12 }}>
              {error}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={(e: FormEvent) => {
          void submit(e)
        }}
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          px: 1.5,
          py: 1.25,
          borderTop: `1px solid ${themeColor.slate10}`,
          background: '#FAFAFC',
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          size="small"
          placeholder={translate('workflow.copilot.placeholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void submit()
            }
          }}
          disabled={loading}
          sx={{
            '& .MuiOutlinedInput-root': {
              'background': '#fff',
              'borderRadius': '10px',
              'fontSize': 13,
              '& fieldset': { borderColor: themeColor.slate10 },
              '&:hover fieldset': { borderColor: aiTokens.accentBorder },
              '&.Mui-focused fieldset': { borderColor: aiTokens.accent, borderWidth: 1 },
            },
          }}
        />
        <IconButton
          type="submit"
          disabled={loading || !input.trim()}
          aria-label={translate('workflow.copilot.send')}
          sx={{
            'width': 36,
            'height': 36,
            'borderRadius': '10px',
            'background': input.trim() && !loading ? aiTokens.gradient : themeColor.slate10,
            'color': '#fff',
            'transition': 'transform 0.15s ease',
            '&:hover': { transform: input.trim() && !loading ? 'translateY(-1px)' : 'none' },
            '&.Mui-disabled': { color: themeColor.slate40 },
          }}
        >
          <SendIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  )
}

export default CopilotPanel
